'use client';
import useArticleId from '@haaretz/s-atoms/articleId';
import useAuthors from '@haaretz/s-atoms/authors';
import usePageType from '@haaretz/s-atoms/pageType';
import usePaywallType from '@haaretz/s-atoms/paywallType';
import usePlatform from '@haaretz/s-atoms/platform';
import useRenderingKind from '@haaretz/s-atoms/renderingKind';
import useUser from '@haaretz/s-atoms/user';
import capitalize from '@haaretz/s-common-utils/capitalize';
import discardNullableValues from '@haaretz/s-common-utils/discardNullableValues';
import { siteAlias } from '@haaretz/s-consts';
import isBlockedPaywall from '@haaretz/s-page-utils/isBlockedPaywall';
import useBiPageType from '@haaretz/s-use-bi-page-type';
import usePpAbTest from '@haaretz/s-use-pp-ab-test';
import useSelectedOffer from '@haaretz/s-use-selected-offer';
import React from 'react';
const noop = ()=>undefined;
export default function useCollectBiData() {
    const _window = typeof window !== 'undefined' ? window : undefined;
    const page_type = useBiPageType();
    // eslint-disable-next-line react-compiler/react-compiler
    const useSelectedOfferData = page_type === 'Promotions' ? useSelectedOffer : noop;
    // eslint-disable-next-line react-compiler/react-compiler
    const useAbTestData = page_type === 'Promotions' ? usePpAbTest : noop;
    const isLogin = page_type === 'Login';
    const user = useUser('cookieValue');
    // eslint-disable-next-line react-compiler/react-compiler
    const selectedOfferData = useSelectedOfferData();
    const platform = usePlatform();
    const articleId = useArticleId();
    const renderingKind = useRenderingKind();
    const paywallType = usePaywallType();
    const authors = useAuthors();
    const pageType = usePageType();
    // eslint-disable-next-line react-compiler/react-compiler
    const abTestData = useAbTestData();
    const currentPageUrl = _window ? new URL(_window.document.URL) : undefined;
    const urlSearchParams = new URLSearchParams(_window?.location.search);
    const { anonymousId: anonymous_id, extendedUserType, userType } = user || {};
    const urlParamsObj = Object.fromEntries(urlSearchParams);
    const { utm_medium, htm_medium, utm_content, htm_content, utm_source, htm_source, utm_campaign, htm_campaign, utm_segment, utm_type, htm_type } = urlParamsObj;
    const memoizedData = React.useMemo(()=>{
        const data = discardNullableValues({
            ab_test_group_label: abTestData?.testGroupLabel,
            ab_test_group: abTestData?.testGroup,
            ab_test_name: abTestData?.testName,
            action_id: noop(),
            ad_block_state: noop(),
            additional_info: noop(),
            anonymous_id,
            article_id: articleId,
            article_type: pageType.endsWith('Article') ? pageType : undefined,
            blocked: isBlockedPaywall(renderingKind),
            brand: siteAlias,
            campaign_details: noop(),
            campaign_name: noop(),
            darkmode: typeof document !== 'undefined' ? document.documentElement.classList.contains('darkMode') : undefined,
            feature_theme: noop(),
            feature_type: isLogin ? 'Marketing' : noop(),
            feature: noop(),
            marketing_element: noop(),
            newsletter_email: noop(),
            newsletter_segment_id: noop(),
            newsletter_segment_name: noop(),
            next_article_id: noop(),
            next_article_list_id: noop(),
            next_article_list_name: noop(),
            next_article_noinlist: noop(),
            next_article_noinpage: noop(),
            next_article_teaser_id: noop(),
            next_article_viewname: noop(),
            page_type,
            paywall_status: paywallType,
            personal_feature: noop(),
            platform: capitalize(platform),
            primary_section: noop(),
            prod_no: selectedOfferData?.productNumber,
            promotions_no: selectedOfferData?.promotionNumber,
            recommendation_type: noop(),
            refactor: true,
            referrer_type: _window?.document?.referrer,
            sale_code: selectedOfferData?.saleCode,
            scroll_height: noop(),
            secondary_section: noop(),
            site: currentPageUrl?.origin,
            stage: noop(),
            url: currentPageUrl?.href,
            user_type: extendedUserType || userType,
            useragent: _window?.navigator.userAgent,
            utm_campaign: utm_campaign || htm_campaign,
            utm_content: utm_content || htm_content,
            utm_medium: utm_medium || htm_medium,
            utm_segment,
            utm_source: utm_source || htm_source,
            utm_type: utm_type || htm_type,
            writer_name: authors?.map((author)=>author.name).toString()
        });
        return data;
    }, [
        abTestData?.testGroupLabel,
        abTestData?.testGroup,
        abTestData?.testName,
        anonymous_id,
        articleId,
        pageType,
        renderingKind,
        isLogin,
        page_type,
        paywallType,
        platform,
        selectedOfferData?.productNumber,
        selectedOfferData?.promotionNumber,
        selectedOfferData?.saleCode,
        _window?.document?.referrer,
        _window?.navigator.userAgent,
        currentPageUrl?.origin,
        currentPageUrl?.href,
        extendedUserType,
        userType,
        utm_campaign,
        htm_campaign,
        utm_content,
        htm_content,
        utm_medium,
        htm_medium,
        utm_segment,
        utm_source,
        htm_source,
        utm_type,
        htm_type,
        authors
    ]);
    return memoizedData;
}

//# sourceMappingURL=useCollectBiData.js.map